<template>
    <span
      v-if="isBillingType"
      v-b-tooltip.hover.right
      :title="isDisabled ? disabledReason || '' : ''"
      class="global-umr-billing-select"
    >
      <b-form-select
        :value="billingTypeSelection"
        :options="BILLING_OPTIONS"
        :disabled="isDisabled || isToggling"
        size="sm"
        @change="handleBillingTypeChange"
      />
    </span>

    <BaseSwitch
      v-else
      :value="$props.value"
      :disabled="isDisabled || isToggling"
      @toggle="handleToggle"
      :tooltip="isDisabled ? disabledReason : ''"
    />
</template>

<script>
import { defineComponent, computed, ref, watch } from '@vue/composition-api'

import { useCurrentUser, useUserCompany, useToast, useTryCatch } from '@composables'
import { LincolnGlobalUMRService } from '@services'
import { BILLING_OPTIONS } from '@constants'

import { BaseSwitch } from '@components/base'

export default defineComponent({
  name: 'GlobalUMRStatusToggle',
  components: {
    BaseSwitch
  },
  props: {
    id: {
      type: Number,
      required: true
    },
    type: {
      type: String,
      default: 'bdx',
      validator: (value) => ['bdx', 'fcp', 'billing', 'rec', 'billing_type'].includes(value)
    },
    currencyType: {
      type: String,
      default: ''
    },
    // The key's scheme (Market/LIMOSS). Used both as the shown value for
    // type="billing_type" and as the gate for type="billing": billing_status
    // can only be turned on once a billing type is set.
    billingType: {
      type: String,
      default: ''
    },
    filterPayload: {
      type: Function,
      default: (payload, status, type) => payload,
      description:
        'Use to filter updateStatus post request payload before is called.'
    },
    // This key's CURRENT fcp_status (item.fcp_status). Only used for type="rec" - per explicit
    // decision, once FCP is on for a key, REC is fully driven by the FCP toggle's own cascade
    // (see BDXManagementTableFCPToggle.vue's resolveRecStatus), so even a superuser's direct REC
    // toggle is blocked here while FCP stays on, rather than letting them fight that cascade.
    fcpStatus: Boolean,
    value: Boolean
  },
  setup(props) {
    const { userEmail, isSuperUser } = useCurrentUser()
    const { isRoleName: isCompanyRoleName } = useUserCompany()
    const { showToast } = useToast()

    const isBDX = computed(() => 'bdx' === props.type)
    const isBillingStatus = computed(() => props.type === 'billing')
    const isBillingType = computed(() => props.type === 'billing_type')
    const isRec = computed(() => props.type === 'rec')

    const isToggling = ref(false)

    // The row isn't re-fetched after a change, so hold the shown option locally.
    const billingTypeSelection = ref(props.billingType)

    watch(
      () => props.billingType,
      (billingType) => {
        billingTypeSelection.value = billingType
      }
    )

    const isDisabled = computed(() => {
      if (isBillingType.value) return !isSuperUser.value
      if (isBillingStatus.value) {
        if (!isSuperUser.value) return true
        if (props.value) return false
        return !props.billingType || !props.currencyType
      }
      // Disable bdx toggle for non-superusers
      if (isBDX.value && !isSuperUser.value) {
        return true
      }
      // Disable rec toggle for non-superusers, or (even for a superuser) whenever FCP is on for
      // this key - see the fcpStatus prop's own comment. Per explicit decision, no longer gated
      // on whether any of this key's associated companies has rec_enabled=1.
      if (isRec.value && (!isSuperUser.value || props.fcpStatus)) {
        return true
      }
      if (isSuperUser.value) return false
      return !isCompanyRoleName('TPA') && !isCompanyRoleName('Underwriter')
    })

    const disabledReason = computed(() => {
      if (isBDX.value && !isSuperUser.value) return 'To update this BDX Status please contact support@limeiq.com.'
      if (isRec.value && !isSuperUser.value) return 'You do not have permission to change the Rec Enabled status.'
      if (isRec.value && isSuperUser.value && props.fcpStatus) {
        return 'Reconciliation is controlled by the FCP status while FCP is on for this key.'
      }
      if (isBillingType.value) {
        return isSuperUser.value ? null : 'You do not have permission to change the billing type.'
      }
      if (!isBillingStatus.value || !isDisabled.value) return null
      if (!isSuperUser.value) return 'You do not have permission to change the billing status.'
      if (!props.billingType) return 'Billing status cannot be enabled because billing type is not set.'
      if (!props.currencyType) return 'Billing status cannot be enabled because the currency type is missing.'
      return null
    })

    // This endpoint returns validation failures as HTTP 200 with the real status
    // in the body, so axios never throws. Without reading it, a rejected update
    // looks successful.
    const postStatus = async (payload) => {
      let outcome = { ok: false, message: null }

      await useTryCatch(async () => {
        const res = await LincolnGlobalUMRService.updateStatus(userEmail.value, [
          payload
        ])
        // Lincoln answers {status, message, data}; tolerate a second wrapper if
        // ever proxied through LimeIQ's renderer.
        const body = res && res.data && res.data.data && res.data.data.status
          ? res.data.data
          : res && res.data

        if (body && body.status && 200 !== body.status) {
          outcome = {
            ok: false,
            message: 'string' === typeof body.data ? body.data : body.message
          }
          return
        }

        outcome = { ok: true, message: null }
      })

      return outcome
    }

    const handleToggle = async (status) => {
      if (isDisabled.value) return

      isToggling.value = true

      const type = props.type

      await postStatus(
        props.filterPayload(
          {
            id: props.id,
            [`${type}_status`]: status,
            ...(!status && isBDX.value ? { bdx_status_pending: 'Close' } : null)
          },
          status,
          type
        )
      )

      isToggling.value = false
    }

    const handleBillingTypeChange = async (selection) => {
      const previousSelection = billingTypeSelection.value

      if (isDisabled.value || selection === previousSelection) return

      billingTypeSelection.value = selection
      isToggling.value = true

      const { ok, message } = await postStatus(
        props.filterPayload(
          { id: props.id, billing_type: selection },
          selection,
          props.type
        )
      )

      // Snap back so the cell never shows a rejected scheme, and say why - the
      // reason (e.g. key is not FCP) exists only in the response. Held past the
      // 3s default so it can actually be read.
      if (!ok) {
        billingTypeSelection.value = previousSelection
        if (message) {
          showToast(message, 'error', {
            duration: 10000,
            action: {
              text: 'Dismiss',
              onClick: (event, toast) => toast.goAway(0)
            }
          })
        }
      }

      isToggling.value = false
    }

    return {
      BILLING_OPTIONS,

      isBillingType,
      isToggling,
      billingTypeSelection,
      handleToggle,
      handleBillingTypeChange,
      isDisabled,
      disabledReason
    }
  }
})
</script>

<style scoped>
.global-umr-billing-select >>> .custom-select {
  min-width: 96px;
  height: 24px;
  padding: 1px 22px 1px 6px;
  font-size: 11px;
  line-height: 1.3;
}
</style>
